import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";

const OR = "#C9A86A";

const T = {
  fr: {
    seoTitle: "Devenir revendeur — Maison Partita",
    seoDesc:
      "Maison Partita ouvre sa distribution. Parfums fabriqués en Espagne, eau de parfum concentrée 50 ml, séries limitées numérotées. Dossier revendeur sur demande.",
    kicker: "Espace professionnel",
    title: "Devenir revendeur",
    intro1:
      "Maison Partita ouvre sa distribution à un petit nombre de points de vente. Nous ne cherchons pas à être partout : nous cherchons des boutiques capables de raconter une maison, pas seulement de poser un flacon sur une étagère.",
    intro2:
      "Nos collections sont produites en séries limitées numérotées. Une référence épuisée ne revient pas systématiquement — c'est le principe de la maison, et c'est aussi ce qui fait qu'un client revient dans votre boutique plutôt que d'attendre une réédition.",
    factsTitle: "La maison en cinq lignes",
    facts: [
      ["Fabrication", "Parfums fabriqués en Espagne, en laboratoire cosmétique agréé."],
      ["Format", "Eau de parfum concentrée, 50 ml, étui imprimé."],
      ["Collections", "CRISOPEA — les sept métaux. DOMINO — vingt-quatre tuiles, deux séries en miroir. DAMES à venir."],
      ["Édition", "Séries limitées numérotées. Réassort non garanti."],
      ["Conformité", "Chaque référence est notifiée au CPNP et dispose de son dossier d'information produit (DIP) et de son rapport de sécurité (CPSR)."],
    ],
    profileTitle: "Qui nous intéresse",
    profile: [
      "Parfumeries indépendantes et concept-stores.",
      "Boutiques d'hôtel, spas et maisons d'hôtes de caractère.",
      "Corners et pop-up stores en France et en Europe.",
      "Distributeurs disposant d'un réseau constitué.",
    ],
    termsNote:
      "Tarifs professionnels, quantité minimale et conditions d'exclusivité territoriale sont communiqués au cas par cas, après un premier échange. Dites-nous d'abord qui vous êtes et ce que vous vendez.",
    formTitle: "Demander le dossier revendeur",
    company: "Enseigne ou société",
    name: "Votre nom",
    email: "Votre adresse e-mail",
    phone: "Téléphone (facultatif)",
    place: "Ville et pays",
    kind: "Type de point de vente",
    kinds: [
      "Parfumerie indépendante",
      "Concept-store",
      "Hôtel, spa, maison d'hôtes",
      "Boutique en ligne",
      "Distributeur / grossiste",
      "Autre",
    ],
    message: "Parlez-nous de votre point de vente : les maisons que vous distribuez déjà, votre clientèle, ce que vous cherchez.",
    send: "Envoyer ma demande",
    sent: "Demande envoyée. Nous revenons vers vous rapidement.",
    made: "Fabriqué en Espagne · Made in Spain",
  },
  en: {
    seoTitle: "Become a stockist — Maison Partita",
    seoDesc:
      "Maison Partita is opening its distribution. Perfumes made in Spain, concentrated eau de parfum 50 ml, numbered limited series. Trade pack on request.",
    kicker: "Trade enquiries",
    title: "Become a stockist",
    intro1:
      "Maison Partita is opening its distribution to a small number of points of sale. We are not looking to be everywhere: we are looking for shops able to tell the story of a house, not merely to place a bottle on a shelf.",
    intro2:
      "Our collections are produced in numbered limited series. A sold-out reference does not systematically return — that is the principle of the house, and it is also what brings a customer back to your shop rather than waiting for a reissue.",
    factsTitle: "The house in five lines",
    facts: [
      ["Manufacture", "Perfumes made in Spain, in a licensed cosmetics laboratory."],
      ["Format", "Concentrated eau de parfum, 50 ml, printed case."],
      ["Collections", "CRISOPEA — the seven metals. DOMINO — twenty-four tiles, two mirrored series. DAMES to come."],
      ["Edition", "Numbered limited series. Restocking not guaranteed."],
      ["Compliance", "Every reference is notified on the CPNP and holds its product information file (PIF) and safety report (CPSR)."],
    ],
    profileTitle: "Who we are looking for",
    profile: [
      "Independent perfumeries and concept stores.",
      "Hotel boutiques, spas and character guest houses.",
      "Corners and pop-up stores across France and Europe.",
      "Distributors with an established network.",
    ],
    termsNote:
      "Trade prices, minimum order quantity and territorial exclusivity are discussed case by case, after a first conversation. Tell us first who you are and what you sell.",
    formTitle: "Request the trade pack",
    company: "Shop or company name",
    name: "Your name",
    email: "Your e-mail address",
    phone: "Phone (optional)",
    place: "City and country",
    kind: "Type of point of sale",
    kinds: [
      "Independent perfumery",
      "Concept store",
      "Hotel, spa, guest house",
      "Online shop",
      "Distributor / wholesaler",
      "Other",
    ],
    message: "Tell us about your point of sale: the houses you already carry, your clientele, what you are looking for.",
    send: "Send my request",
    sent: "Request sent. We will come back to you shortly.",
    made: "Made in Spain · Fabriqué en Espagne",
  },
};

const VIDE = { company: "", name: "", email: "", phone: "", place: "", kind: "", message: "" };

export default function Revendeurs() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const [form, setForm] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // On réutilise l'endpoint /contact : les champs du dossier revendeur sont
      // repliés dans le message, ce qui évite un second script côté serveur.
      const corps = [
        `DEMANDE REVENDEUR (${lang.toUpperCase()})`,
        `Enseigne : ${form.company}`,
        `Ville / pays : ${form.place}`,
        `Type de point de vente : ${form.kind || "non précisé"}`,
        `Téléphone : ${form.phone || "non communiqué"}`,
        "",
        form.message,
      ].join("\n");
      const { data } = await api.post("/contact", {
        name: `REVENDEUR — ${form.company} (${form.name})`.slice(0, 120),
        email: form.email,
        message: corps,
      });
      toast.success(data.message || t.sent);
      setForm(VIDE);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-16">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase" style={{ color: OR }}>{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.08em] uppercase text-zinc-50">
            {t.title}
          </h1>
          <p className="mt-10 text-[19px] leading-[1.85] text-zinc-300">{t.intro1}</p>
          <p className="mt-7 text-[19px] leading-[1.85] text-zinc-300">{t.intro2}</p>
        </Reveal>
      </section>

      {/* MADE IN SPAIN — bandeau assumé, c'est ce que regarde un acheteur pro */}
      <div
        className="py-5 text-center text-[12px] sm:text-[13px] tracking-[0.34em] uppercase border-y"
        style={{ color: OR, borderColor: "rgba(201,168,106,0.22)" }}
        data-testid="made-in-spain"
      >
        {t.made}
      </div>

      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-20">
        <Reveal>
          <h2 className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.factsTitle}</h2>
          <dl className="mt-10 divide-y" style={{ borderColor: "rgba(201,168,106,0.15)" }}>
            {t.facts.map(([k, v]) => (
              <div key={k} className="py-6 sm:flex sm:gap-10" style={{ borderTop: "1px solid rgba(201,168,106,0.15)" }}>
                <dt className="w-44 shrink-0 text-[13px] tracking-[0.2em] uppercase" style={{ color: OR }}>{k}</dt>
                <dd className="mt-2 sm:mt-0 text-[17px] leading-[1.75] text-zinc-300">{v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-20">
        <Reveal>
          <h2 className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.profileTitle}</h2>
          <ul className="mt-8 space-y-4">
            {t.profile.map((p) => (
              <li key={p} className="flex gap-5 text-[17px] leading-[1.75] text-zinc-300">
                <span style={{ color: OR }}>—</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 max-w-2xl text-[15px] leading-[1.8] text-zinc-500">{t.termsNote}</p>
        </Reveal>
      </section>

      <section className="max-w-2xl mx-auto px-5 sm:px-10 pt-24 pb-32">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl tracking-[0.06em] text-zinc-50">{t.formTitle}</h2>
          <form onSubmit={submit} className="mt-12 space-y-9" data-testid="revendeur-form">
            <input required value={form.company} onChange={set("company")} placeholder={t.company}
              className="val-input" data-testid="revendeur-company" />
            <input required value={form.name} onChange={set("name")} placeholder={t.name}
              className="val-input" data-testid="revendeur-name" />
            <input type="email" required value={form.email} onChange={set("email")} placeholder={t.email}
              className="val-input" data-testid="revendeur-email" />
            <input value={form.phone} onChange={set("phone")} placeholder={t.phone}
              className="val-input" data-testid="revendeur-phone" />
            <input required value={form.place} onChange={set("place")} placeholder={t.place}
              className="val-input" data-testid="revendeur-place" />
            <select required value={form.kind} onChange={set("kind")}
              className="val-input" data-testid="revendeur-kind"
              style={{ color: form.kind ? undefined : "rgba(161,161,170,0.6)" }}>
              <option value="" disabled>{t.kind}</option>
              {t.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <textarea required rows={5} value={form.message} onChange={set("message")} placeholder={t.message}
              className="val-input resize-none" data-testid="revendeur-message" />
            <button type="submit" disabled={busy} className="val-btn" data-testid="revendeur-submit">
              {busy ? "…" : t.send}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
